import React from "react"
import { useTranslation } from "next-i18next"

import { CheckCircleFill } from "@styled-icons/bootstrap/CheckCircleFill"
import { TrialBuyManageButton } from "./SubcriptionDetails"
import plans from "../functions/plans"
import Link from "next/link"
import LoadingButton, { Button } from "./LoadingButton"

const Feature = ({ children }) => (
  <li className="flex items-center font-heading mb-4 font-medium text-base text-gray-900">
    <svg className="mr-2.5 shrink-0" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z" fill="#14B082"/>
    </svg>
    {children}
  </li>
)

const DisabledFeature = ({ children }) => (
  <li className="flex items-center font-heading mb-4 font-medium text-base text-gray-400">
    <svg className="mr-2.5" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M4.29289 4.29289C4.68342 3.90237 5.31658 3.90237 5.70711 4.29289L10 8.58579L14.2929 4.29289C14.6834 3.90237 15.3166 3.90237 15.7071 4.29289C16.0976 4.68342 16.0976 5.31658 15.7071 5.70711L11.4142 10L15.7071 14.2929C16.0976 14.6834 16.0976 15.3166 15.7071 15.7071C15.3166 16.0976 14.6834 16.0976 14.2929 15.7071L10 11.4142L5.70711 15.7071C5.31658 16.0976 4.68342 16.0976 4.29289 15.7071C3.90237 15.3166 3.90237 14.6834 4.29289 14.2929L8.58579 10L4.29289 5.70711C3.90237 5.31658 3.90237 4.68342 4.29289 4.29289Z" fill="#A1A1AA"/>
    </svg>
    {children}
  </li>
)

const FeatureWithTooltip = ({ children, tooltip, image }) => (
  <li className="flex items-center font-heading mb-4 font-medium text-base text-gray-900 relative">
    <svg className="mr-2.5 shrink-0" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z" fill="#14B082"/>
    </svg>
    {children}
    <span className="relative cursor-pointer flex items-center group">
      <svg className="ml-1 w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
        <path d="M12 16v-2m0-4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <div className="hidden group-hover:block absolute z-50 w-64 p-4 text-xs text-gray-800 bg-gray-100 rounded-lg shadow-md tooltip shadow-3xl" style={{top: '-5px', left: '50%', transform: 'translateX(-80%)'}}>
        <p>{tooltip}</p>
        {image && <img src={image} alt="Feature example" className="mt-4 rounded-lg max-w-full h-auto"/>}
      </div>
    </span>
  </li>
)

const FeatureSection = ({ title, children }) => (
  <>
    <h4 className="text-lg font-heading font-semibold mb-4 text-black">{title}</h4>
    <ul className="mb-6">
      {children}
    </ul>
  </>
)

const PricingGrid = () => {
  const { t } = useTranslation()
  const [isMonthly, setIsMonthly] = React.useState(false)

  const calculateMonthlyPrice = (yearlyPrice) => {
    return Math.round(yearlyPrice / 12)
  }

  return (
    <section className="overflow-hidden py-24">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto mb-16 relative">
          <div className="bg-white rounded-2xl shadow-xl border-2 border-pink-100 overflow-hidden relative">
            <div className="absolute top-0 right-0 bg-pink-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
              Black Friday Limited Time Offer
            </div>
            <div className="p-8 sm:p-10">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-2">
                    Lifetime Access
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Pay once, own it forever. Get all Personal plan features with no monthly fees.
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 mb-6 justify-center md:justify-start">
                    <div className="flex items-center text-sm text-gray-700">
                      <svg className="mr-2 shrink-0" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z" fill="#14B082"/>
                      </svg>
                      50 AI Generations/mo
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <svg className="mr-2 shrink-0" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z" fill="#14B082"/>
                      </svg>
                      10 Boards/mo
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <svg className="mr-2 shrink-0" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z" fill="#14B082"/>
                      </svg>
                      200 Imports
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 text-center">
                  <div className="mb-2">
                    <span className="text-4xl sm:text-5xl font-heading font-bold text-gray-900">$149</span>
                    <span className="text-gray-500 text-sm block mt-1">One-time payment</span>
                  </div>
                  <div className="w-full sm:w-auto">
                    <TrialBuyManageButton 
                      plan={{
                        ...plans.personal,
                        name: 'Lifetime Deal',
                        paymentMode: 'payment',
                        stripePriceId: plans.personal.stripePriceIdLifetime,
                        price: plans.personal.lifetimePrice
                      }} 
                      highlight={true} 
                      isMonthly={true}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Regular value $360
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-xl mx-auto">
          <h2 className="mb-9 font-heading text-center text-gray-900 text-5xl sm:text-7xl md:text-9xl xl:text-10xl">
            Choose Your Plan
          </h2>
          
          <div className="flex flex-wrap justify-center -m-5 mb-20">
            <div className="w-auto p-2">
              <div className="flex items-center font-heading font-medium text-gray-600 text-base">
                <input 
                  className="w-4 h-4 mr-2" 
                  id="radio1" 
                  type="radio" 
                  name="billing"
                  checked={!isMonthly}
                  onChange={() => setIsMonthly(false)}
                />
                <label htmlFor="radio1">
                  <span>Annually</span>
                  <span className="text-pink-500 ml-2">(Save up to 35%)</span>
                </label>
              </div>
            </div>
            <div className="w-auto p-2">
              <div className="flex items-center font-heading font-medium text-gray-600 text-base">
                <input 
                  className="w-4 h-4 mr-2" 
                  id="radio2" 
                  type="radio" 
                  name="billing"
                  checked={isMonthly}
                  onChange={() => setIsMonthly(true)}
                />
                <label htmlFor="radio2">Monthly</label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap -m-3">
          {/* Free Plan */}
          <div className="w-full md:w-1/2 xl:w-1/4 p-3">
            <div className="border-gray-200 rounded-xl shadow-lg border-2 px-6 py-8">
              <h3 className="mb-9 font-heading font-medium text-base text-black">Free</h3>
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-baseline mb-2">
                  <span className="mr-1 mt-0.5 font-heading font-semibold text-lg text-gray-900">$</span>
                  <span className="font-heading font-bold text-6xl sm:text-7xl text-gray-900">0</span>
                  <span className="text-sm text-gray-700 self-end">/ Month</span>
                </div>
                <p className="text-sm text-gray-600 mb-2 invisible">
                  Spacer for alignment
                </p>
                <a href="/new-board" rel="nofollow" className="p-1 w-full block">
                  <div className="group relative flex items-center font-heading block py-2 px-5 text-sm sm:text-base rounded-full cursor-pointer font-heading hover:ring-2 ring-2 ring-gray-700 hover:ring-gray-500 mx-auto md:mx-0">
                    <p className="relative z-10 font-heading text-base text-black mx-auto">Start For Free</p>
                  </div>
                </a>
              </div>

              <FeatureSection title="MyRoomDesigner.AI & Mood Board AI">
                <DisabledFeature>AI Generations</DisabledFeature>
              </FeatureSection>

              <FeatureSection title="Mood Board Editor">
                <Feature>1 x Board / Month</Feature>
                <Feature>3 x One-Click Custom Product Import</Feature>
                <Feature>1 x AI Background Removal</Feature>
                <Feature>Furniture Product Library</Feature>
                <FeatureWithTooltip 
                  tooltip="As you create a mood board, this feature automatically lists all added items with their prices and provides a summarized total cost for easy tracking."
                  image="https://static.shuffle.dev/uploads/files/68/685cdf6531559900d35917105bd95b994e60c466/Shopping-List-Creator.png"
                >
                  Automated Shopping List Creator
                </FeatureWithTooltip>
              </FeatureSection>

              <FeatureSection title="Extra Features">
                <Feature>Color Palette Generator</Feature>
              </FeatureSection>
            </div>
          </div>



          {/* Personal Plan */}
          <div className="w-full md:w-1/2 xl:w-1/4 p-3">
            <div className="relative rounded-xl bg-[#f7f6f4] ring-2 ring-gray-900 shadow-lg p-8">
              <p className="absolute right-2 top-2 px-2.5 py-1 text-xs max-w-max uppercase tracking-px rounded-full text-gray-900 bg-pink-100">
                Popular choice
              </p>
              <h3 className="mb-9 font-heading font-medium text-base text-black">{t("pricing.personal")}</h3>
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-baseline">
                  <span className="mr-1 mt-0.5 font-heading font-semibold text-lg text-gray-900">$</span>
                  {!isMonthly && (
                    <span className="font-heading text-6xl sm:text-7xl text-gray-400 line-through mr-4">29</span>
                  )}
                  <span className="font-heading font-bold text-6xl sm:text-7xl text-gray-900">
                    {isMonthly ? '29' : '15'}
                  </span>
                  <span className="text-sm text-gray-700 self-end ml-2">/ Month</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {isMonthly ? 'Billed monthly' : 'Billed $180 yearly'}
                </p>
                <TrialBuyManageButton 
                  plan={plans.personal} 
                  highlight={true} 
                  isMonthly={isMonthly}
                />
              </div>

              <FeatureSection title="MyRoomDesigner.AI & Mood Board AI">
                <Feature>50 x AI Generations / Month</Feature>
                <FeatureWithTooltip 
                  tooltip="Receive an AI-generated description of your uploaded photo to enhance the prompt for more accurate and tailored image generation."
                  image="https://static.shuffle.dev/uploads/files/68/685cdf6531559900d35917105bd95b994e60c466/Step2.png"
                >
                  AI-Generated Photo Description
                </FeatureWithTooltip>
                <Feature>'Upscale', 'Clean-Up' and 'Find Similar Products' Features</Feature>
              </FeatureSection>

              <FeatureSection title="Mood Board Editor">
                <Feature>10 x Boards / Month</Feature>
                <Feature>{plans.personal.importLimit} x One-Click Custom Product Import / Month</Feature>
                <Feature>{plans.personal.backgroundLimit} x AI Background Removal</Feature>
                <Feature>Furniture Product Library</Feature>
                <FeatureWithTooltip 
                  tooltip="As you create a mood board, this feature automatically lists all added items with their prices and provides a summarized total cost for easy tracking."
                  image="https://static.shuffle.dev/uploads/files/68/685cdf6531559900d35917105bd95b994e60c466/Shopping-List-Creator.png"
                >
                  Automated Shopping List Creator
                </FeatureWithTooltip>
              </FeatureSection>

              <FeatureSection title="Extra Features">
                <Feature>Color Palette Generator</Feature>
              </FeatureSection>
            </div>
          </div>

          {/* Business Plan */}
          <div className="w-full md:w-1/2 xl:w-1/4 p-3">
            <div className="border-gray-200 rounded-xl shadow-lg border-2 px-6 py-8">
              <h3 className="mb-9 font-heading font-medium text-base text-black">{t("pricing.business")}</h3>
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-baseline">
                  <span className="mr-1 mt-0.5 font-heading font-semibold text-lg text-gray-900">$</span>
                  {!isMonthly && (
                    <span className="font-heading text-6xl sm:text-7xl text-gray-400 line-through mr-4">49</span>
                  )}
                  <span className="font-heading font-bold text-6xl sm:text-7xl text-gray-900">
                    {isMonthly ? '49' : '39'}
                  </span>
                  <span className="text-sm text-gray-700 self-end ml-2">/ Month</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {isMonthly ? 'Billed monthly' : 'Billed $468 yearly'}
                </p>
                <TrialBuyManageButton plan={plans.business} isMonthly={isMonthly} />
              </div>

              <FeatureSection title="MyRoomDesigner.AI & Mood Board AI">
                <Feature>{plans.business.generateAiLimit} x AI Generations / Month</Feature>
                <FeatureWithTooltip 
                  tooltip="Receive an AI-generated description of your uploaded photo to enhance the prompt for more accurate and tailored image generation."
                  image="https://static.shuffle.dev/uploads/files/68/685cdf6531559900d35917105bd95b994e60c466/Step2.png"
                >
                  AI-Generated Photo Description
                </FeatureWithTooltip>
                <Feature>'Upscale', 'Clean-Up' and 'Find Similar Products' Features</Feature>
              </FeatureSection>

              <FeatureSection title="Mood Board Editor">
                <Feature>100 x Boards / Month</Feature>
                <Feature>{plans.business.importLimit} x One-Click Custom Product Import / Month</Feature>
                <Feature>{plans.business.backgroundLimit} x AI Background Removal</Feature>
                <Feature>Furniture Product Library</Feature>
                <FeatureWithTooltip 
                  tooltip="As you create a mood board, this feature automatically lists all added items with their prices and provides a summarized total cost for easy tracking."
                  image="https://static.shuffle.dev/uploads/files/68/685cdf6531559900d35917105bd95b994e60c466/Shopping-List-Creator.png"
                >
                  Automated Shopping List Creator
                </FeatureWithTooltip>
              </FeatureSection>

              <FeatureSection title="Extra Features">
                <Feature>Color Palette Generator</Feature>
              </FeatureSection>
            </div>
          </div>

          {/* Premium Plan */}
          <div className="w-full md:w-1/2 xl:w-1/4 p-3">
            <div className="border-gray-200 rounded-xl shadow-lg border-2 px-6 py-8">
              <h3 className="mb-9 font-heading font-medium text-base text-black">Premium</h3>
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-baseline">
                  <span className="mr-1 mt-0.5 font-heading font-semibold text-lg text-gray-900">$</span>
                  {!isMonthly && (
                    <span className="font-heading text-6xl sm:text-7xl text-gray-400 line-through mr-4">89</span>
                  )}
                  <span className="font-heading font-bold text-6xl sm:text-7xl text-gray-900">
                    {isMonthly ? '89' : '75'}
                  </span>
                  <span className="text-sm text-gray-700 self-end ml-2">/ Month</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {isMonthly ? 'Billed monthly' : 'Billed $900 yearly'}
                </p>
                <TrialBuyManageButton plan={plans.premium} isMonthly={isMonthly} />
              </div>

              <FeatureSection title="MyRoomDesigner.AI & Mood Board AI">
                <Feature>{plans.premium.generateAiLimit} x AI Generations / Month</Feature>
                <FeatureWithTooltip 
                  tooltip="Receive an AI-generated description of your uploaded photo to enhance the prompt for more accurate and tailored image generation."
                  image="https://static.shuffle.dev/uploads/files/68/685cdf6531559900d35917105bd95b994e60c466/Step2.png"
                >
                  AI-Generated Photo Description
                </FeatureWithTooltip>
                <Feature>'Upscale', 'Clean-Up' and 'Find Similar Products' Features</Feature>
              </FeatureSection>

              <FeatureSection title="Mood Board Editor">
                <Feature>200 x Boards / Month</Feature>
                <Feature>{plans.premium.importLimit} x One-Click Custom Product Import / Month</Feature>
                <Feature>{plans.premium.backgroundLimit} x AI Background Removal</Feature>
                <Feature>Furniture Product Library</Feature>
                <FeatureWithTooltip 
                  tooltip="As you create a mood board, this feature automatically lists all added items with their prices and provides a summarized total cost for easy tracking."
                  image="https://static.shuffle.dev/uploads/files/68/685cdf6531559900d35917105bd95b994e60c466/Shopping-List-Creator.png"
                >
                  Automated Shopping List Creator
                </FeatureWithTooltip>
              </FeatureSection>

              <FeatureSection title="Extra Features">
                <Feature>Color Palette Generator</Feature>
              </FeatureSection>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default PricingGrid
